import { useState, useCallback } from "react"

// Enhanced form validation utilities
export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  custom?: (value: any) => string | null
  email?: boolean
  phone?: boolean
  zipCode?: boolean
  url?: boolean
  numeric?: boolean
  positive?: boolean
  futureDate?: boolean
  pastDate?: boolean
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

export interface FieldValidation {
  value: any
  rules: ValidationRule
  fieldName: string
}

// Common validation patterns
export const PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[\+]?[1-9][\d]{0,15}$/,
  ZIP_CODE: /^\d{5}(-\d{4})?$/,
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  ALPHANUMERIC: /^[a-zA-Z0-9\s]+$/,
  ALPHABETIC: /^[a-zA-Z\s]+$/,
  NUMERIC: /^\d+$/,
  DECIMAL: /^\d+(\.\d+)?$/
} as const

// Common error messages
export const ERROR_MESSAGES = {
  REQUIRED: (fieldName: string) => `${fieldName} is required.`,
  MIN_LENGTH: (fieldName: string, min: number) => `${fieldName} must be at least ${min} characters long.`,
  MAX_LENGTH: (fieldName: string, max: number) => `${fieldName} must be no more than ${max} characters long.`,
  PATTERN: (fieldName: string) => `${fieldName} format is invalid.`,
  EMAIL: (fieldName: string) => `Please enter a valid email address.`,
  PHONE: (fieldName: string) => `Please enter a valid phone number.`,
  ZIP_CODE: (fieldName: string) => `Please enter a valid ZIP code.`,
  URL: (fieldName: string) => `Please enter a valid URL.`,
  NUMERIC: (fieldName: string) => `${fieldName} must be a number.`,
  POSITIVE: (fieldName: string) => `${fieldName} must be a positive number.`,
  FUTURE_DATE: (fieldName: string) => `${fieldName} must be a future date.`,
  PAST_DATE: (fieldName: string) => `${fieldName} must be a past date.`,
  CUSTOM: (fieldName: string, message: string) => message
} as const

// Validate a single field
export function validateField(field: FieldValidation): ValidationResult {
  const { value, rules, fieldName } = field
  const errors: string[] = []

  // Required validation
  if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    errors.push(ERROR_MESSAGES.REQUIRED(fieldName))
    return { isValid: false, errors }
  }

  // Skip other validations if value is empty and not required
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return { isValid: true, errors: [] }
  }

  const stringValue = String(value).trim()

  // Length validations
  if (rules.minLength && stringValue.length < rules.minLength) {
    errors.push(ERROR_MESSAGES.MIN_LENGTH(fieldName, rules.minLength))
  }

  if (rules.maxLength && stringValue.length > rules.maxLength) {
    errors.push(ERROR_MESSAGES.MAX_LENGTH(fieldName, rules.maxLength))
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    errors.push(ERROR_MESSAGES.PATTERN(fieldName))
  }

  // Email validation
  if (rules.email && !PATTERNS.EMAIL.test(stringValue)) {
    errors.push(ERROR_MESSAGES.EMAIL(fieldName))
  }

  // Phone validation
  if (rules.phone && !PATTERNS.PHONE.test(stringValue.replace(/[\s\-\(\)]/g, ''))) {
    errors.push(ERROR_MESSAGES.PHONE(fieldName))
  }

  // ZIP code validation
  if (rules.zipCode && !PATTERNS.ZIP_CODE.test(stringValue)) {
    errors.push(ERROR_MESSAGES.ZIP_CODE(fieldName))
  }

  // URL validation
  if (rules.url && !PATTERNS.URL.test(stringValue)) {
    errors.push(ERROR_MESSAGES.URL(fieldName))
  }

  // Numeric validation
  if (rules.numeric) {
    const numValue = Number(value)
    if (isNaN(numValue)) {
      errors.push(ERROR_MESSAGES.NUMERIC(fieldName))
    } else if (rules.positive && numValue <= 0) {
      errors.push(ERROR_MESSAGES.POSITIVE(fieldName))
    }
  }

  // Date validations
  if (rules.futureDate || rules.pastDate) {
    const dateValue = new Date(value)
    const now = new Date()
    
    if (isNaN(dateValue.getTime())) {
      errors.push(ERROR_MESSAGES.PATTERN(fieldName))
    } else if (rules.futureDate && dateValue <= now) {
      errors.push(ERROR_MESSAGES.FUTURE_DATE(fieldName))
    } else if (rules.pastDate && dateValue >= now) {
      errors.push(ERROR_MESSAGES.PAST_DATE(fieldName))
    }
  }

  // Custom validation
  if (rules.custom) {
    const customError = rules.custom(value)
    if (customError) {
      errors.push(ERROR_MESSAGES.CUSTOM(fieldName, customError))
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

// Validate multiple fields
export function validateForm(fields: FieldValidation[]): ValidationResult {
  const allErrors: string[] = []
  let allValid = true

  for (const field of fields) {
    const result = validateField(field)
    if (!result.isValid) {
      allValid = false
      allErrors.push(...result.errors)
    }
  }

  return {
    isValid: allValid,
    errors: allErrors
  }
}

// Real-time validation helpers
export function getFieldError(value: any, rules: ValidationRule, fieldName: string): string | null {
  const result = validateField({ value, rules, fieldName })
  return result.errors.length > 0 ? result.errors[0] : null
}

export function isFieldValid(value: any, rules: ValidationRule, fieldName: string): boolean {
  const result = validateField({ value, rules, fieldName })
  return result.isValid
}

// Common validation rules
export const VALIDATION_RULES = {
  EMAIL: { required: true, email: true } as ValidationRule,
  PHONE: { required: true, phone: true } as ValidationRule,
  ZIP_CODE: { zipCode: true } as ValidationRule,
  URL: { url: true } as ValidationRule,
  REQUIRED: { required: true } as ValidationRule,
  PASSWORD: { 
    required: true, 
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/ 
  } as ValidationRule,
  NAME: { 
    required: true, 
    pattern: PATTERNS.ALPHABETIC,
    minLength: 2 
  } as ValidationRule,
  TITLE: { 
    required: true, 
    minLength: 3,
    maxLength: 100 
  } as ValidationRule,
  DESCRIPTION: { 
    required: true, 
    minLength: 10,
    maxLength: 500 
  } as ValidationRule
} as const

// Form validation hook for React
export function useFormValidation<T extends Record<string, any>>(
  initialValues: T,
  validationRules: Record<keyof T, ValidationRule>
) {
  const [values, setValues] = useState<T>(initialValues)
  const [errors, setErrors] = useState<Record<keyof T, string[]>>({} as Record<keyof T, string[]>)
  const [touched, setTouched] = useState<Record<keyof T, boolean>>({} as Record<keyof T, boolean>)

  const validateFieldLocal = useCallback((name: keyof T, value: any) => {
    const fieldValidation = validateField({
      value,
      rules: validationRules[name],
      fieldName: String(name)
    })
    
    setErrors(prev => ({
      ...prev,
      [name]: fieldValidation.errors
    }))
    
    return fieldValidation.isValid
  }, [validationRules])

  const handleChange = useCallback((name: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [name]: value }))
    
    if (touched[name]) {
      validateFieldLocal(name, value)
    }
  }, [touched, validateFieldLocal])

  const handleBlur = useCallback((name: keyof T) => {
    setTouched(prev => ({ ...prev, [name]: true }))
    validateFieldLocal(name, values[name])
  }, [validateFieldLocal, values])

  const validateFormLocal = useCallback(() => {
    const fields: FieldValidation[] = Object.keys(validationRules).map(key => ({
      value: values[key as keyof T],
      rules: validationRules[key as keyof T],
      fieldName: key
    }))

    const result = validateForm(fields)
    
    // Update errors for all fields
    const newErrors: Record<keyof T, string[]> = {} as Record<keyof T, string[]>
    Object.keys(validationRules).forEach(key => {
      const fieldKey = key as keyof T
      const fieldValidation = validateField({
        value: values[fieldKey],
        rules: validationRules[fieldKey],
        fieldName: key
      })
      newErrors[fieldKey] = fieldValidation.errors
    })
    
    setErrors(newErrors)
    setTouched(Object.keys(validationRules).reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<keyof T, boolean>))
    
    return result.isValid
  }, [values, validationRules])

  const reset = useCallback(() => {
    setValues(initialValues)
    setErrors({} as Record<keyof T, string[]>)
    setTouched({} as Record<keyof T, boolean>)
  }, [initialValues])

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm: validateFormLocal,
    reset,
    isValid: Object.values(errors).every(errorArray => errorArray.length === 0)
  }
} 
